import { Alert } from 'antd';
import StoryItemEditor from 'components/GuideEditor/StoryItemEditor';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { initGuideStories, notifyGuideStoriesChange, resetGuideStories } from 'store/guide-editor/actions';
import { getCurrentGuide, getCurrentStoriesBeingEdited } from 'store/guide-editor/selectors';
import {
    getErrorLoadingStoriesForGuide,
    isLoadingStoriesForGuide,
    isSavingStoryItems as isSavingStoryItemsSelector,
} from 'store/story-items/selectors';
import { applyPatchToItem } from 'utils/sortable-item-utils';
import { useSelector } from 'utils/use-selector';
import { v4 as uuid } from 'uuid';
import { SortableList } from './SortableList';
import { useSort } from './SortableList/use-sort';

function GuideEditorStory() {
    const dispatch = useDispatch();
    const currentGuide = useSelector(getCurrentGuide);
    const guideId = useMemo(() => currentGuide?.id, [currentGuide]);
    useEffect(() => {
        if (guideId) {
            dispatch(initGuideStories(guideId));
        }
        return () => {
            dispatch(resetGuideStories());
        };
    }, [guideId, dispatch]);

    const currentStories = useSelector(getCurrentStoriesBeingEdited);
    const isLoading = useSelector((state) => isLoadingStoriesForGuide(state, guideId));
    const errorLoading = useSelector((state) => getErrorLoadingStoriesForGuide(state, guideId));
    const isSavingStoryItems = useSelector(isSavingStoryItemsSelector);
    /** @type {[number, React.Dispatch<number>]}*/
    const [storyToScrollTo, setStoryToScrollTo] = useState(null);
    useEffect(() => {
        if (storyToScrollTo) {
            const allStories = document.body.querySelectorAll('.js-story-item-editor');
            if (!allStories) {
                return;
            }
            allStories[allStories.length - 1]?.scrollIntoView({
                behavior: 'smooth',
            });
        }
    }, [storyToScrollTo]);

    const { onMoveUp, onMoveDown } = useSort(currentStories);

    const addStory = () => {
        const newStory = getEmptyStory(currentGuide, currentStories);
        const newStories = currentStories.concat([newStory]);
        dispatch(notifyGuideStoriesChange(newStories));
        setStoryToScrollTo(1 + (storyToScrollTo || 0));
    };

    /**
     * @param {import('utils/sortable-item-utils').ItemFieldChange[]} changes
     */
    const patchStoriesFields = (changes) => {
        let stories = currentStories;
        for (const change of changes) {
            stories = applyPatchToItem(stories, change);
        }
        dispatch(notifyGuideStoriesChange(stories));
    };

    /**
     * @param {import('utils/sortable-item-utils').ItemFieldChange} change
     */
    const patchStoryField = (change) => {
        patchStoriesFields([change]);
    };

    /**
     * @param {StoryItem} story
     */
    const moveStoryUp = (story) => {
        const sortedItems = onMoveUp(story);

        if (sortedItems) {
            patchStoriesFields(
                sortedItems.map((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                }))
            );
        }
    };

    /**
     * @param {StoryItem} story
     */
    const moveStoryDown = (story) => {
        const sortedItems = onMoveDown(story);

        if (sortedItems) {
            patchStoriesFields(
                sortedItems.map((item) => ({
                    id: item.id,
                    fieldName: 'order',
                    newValue: item.newOrder,
                }))
            );
        }
    };

    /**
     * @param {StoryItem} story
     */
    const removeStory = ({ id: storyId }) => {
        const newStories = currentStories.filter((story) => story.id !== storyId);
        dispatch(notifyGuideStoriesChange(newStories));
    };

    if (!currentGuide) {
        return null;
    }

    return (
        <>
            {errorLoading && (
                <div className='full-page-tabs-error'>
                    <Alert
                        className='grow-full-flex'
                        message='There was an error loading story items'
                        description={errorLoading}
                        type='error'
                        showIcon
                    />
                </div>
            )}
            <div className='guide-editor-read-only-content'>
                <SortableList
                    title='Stories'
                    data={currentStories}
                    onAdd={addStory}
                    onDelete={removeStory}
                    onMoveDown={moveStoryDown}
                    onMoveUp={moveStoryUp}
                    isLoading={isLoading || isSavingStoryItems}
                    disableFirstItem
                    isReadOnly>
                    {({ data, index }) =>
                        !data ? (
                            <SortableList.EmptyState message='No Stories' action={null} />
                        ) : (
                            <StoryItemEditor
                                story={data}
                                index={index}
                                guide={currentGuide}
                                onFieldChanged={(storyId, fieldName, newValue) =>
                                    patchStoryField({
                                        id: storyId,
                                        fieldName,
                                        newValue,
                                    })
                                }
                            />
                        )
                    }
                </SortableList>
            </div>
        </>
    );
}

/**
 * @param {Guide} guide
 * @param {StoryItem[]} otherStories
 * @returns {StoryItem}
 */
function getEmptyStory(guide, otherStories) {
    return {
        id: uuid(),
        guide_id: guide.id,
        spot: null,
        cms_media: null,
        tagline: '',
        order: otherStories.length,
        editor_notes: '',
    };
}

export default GuideEditorStory;
